import { Users } from '@huspy/briks-icons';
import { useTranslation } from 'react-i18next';
import { Box, Flex } from '@huspy/briks-web';
import { SectionHeader } from '@modules/opportunities/presentation/pages/CaseCreation/components/CaseInputs';
import {
  FormInput,
  FormSelectInput,
} from '@huspy/forge';
import {
  NATIONALITIES,
  RELATIONSHIP,
  getEmploymentType,
  getResidencyType,
  getBonusTypes,
  getYesNo,
} from '@modules/opportunities/presentation/pages/const';
import { OPPORTUNITY_CURRENCY } from '@modules/opportunities/entities/opportunity/const';
import { UseFormReturnType } from '@mantine/form';
import { BusinessIdentifier } from '@modules/core/api/types';
import { BUSINESS_IDENTIFIER } from '@modules/core/api/const';
import Radio from '@components/v2/Radio';
import { useState } from 'react';

const OtherApplicantInfo = ({
  form,
  isSomeCasesSubmitted,
  businessUnit,
}: {
  form: UseFormReturnType<any>;
  isSomeCasesSubmitted: boolean;
  businessUnit: BusinessIdentifier;
}) => {
  const { t } = useTranslation();
  const isUAE = businessUnit === BUSINESS_IDENTIFIER.AE_HUSPY;
  const isSpain = businessUnit === BUSINESS_IDENTIFIER.ES_BAYTECA;
  const [hasBonus, setHasBonus] = useState(form.getValues().applicant_has_a_bonus);
  return (
    <>
      <SectionHeader
        title={ t('clients.formSections.applicantInfo') }
        Icon={ Users }
      />
      <Flex gap='4'>
        <FormInput
          name='applicant_first_name'
          data-test='input-applicant-firstname'
          label={ t('clients.formLabels.firstName') }
          form={ form }
          disabled={ isSomeCasesSubmitted }
        />
        <FormInput
          name='applicant_last_name'
          data-test='input-applicant-lastname'
          label={ t('clients.formLabels.lastName') }
          form={ form }
          disabled={ isSomeCasesSubmitted }
        />
      </Flex>
      <Flex gap='4'>
        <FormInput
          name='applicant_email'
          data-test='input-applicant-email'
          label={ t('clients.formLabels.email') }
          form={ form }
          disabled={ isSomeCasesSubmitted }
        />
        <FormInput
          name='applicant_mobile'
          data-test='input-applicant-mobile'
          label={ t('clients.formLabels.phoneNumber') }
          form={ form }
          type='phone'
          disabled={ isSomeCasesSubmitted }
        />
      </Flex>
      <Flex gap='4'>
        <FormSelectInput
          name='applicant_citizen_status'
          label={ t('clients.formLabels.residencyStatus') }
          form={ form }
          data={ getResidencyType() }
          disabled={ isSomeCasesSubmitted }
        />
        <FormSelectInput
          name='applicant_employment_status'
          label={ t('clients.formLabels.employmentStatus') }
          form={ form }
          data={ getEmploymentType() }
          disabled={ isSomeCasesSubmitted }
        />
      </Flex>
      <Flex gap='4'>
        <FormInput
          name='applicant_dob'
          data-test='select-applicant-dob'
          label={ t('clients.formLabels.dob') }
          form={ form }
          type='date'
          disabled={ isSomeCasesSubmitted }
        />
        <FormInput
          name='applicant_salary'
          label={ t('clients.formLabels.fixedMonthlySalary') }
          data-test='input-applicant-monthly-salary'
          type='number'
          form={ form }
          trail={ isUAE ? OPPORTUNITY_CURRENCY.AED : OPPORTUNITY_CURRENCY.EUR }
          disabled={ isSomeCasesSubmitted }
        />
      </Flex>
      {isUAE && (
        <Flex gap='4'>
          <FormSelectInput
            name='applicant_nationality'
            label={ t('clients.formLabels.nationality') }
            data={ NATIONALITIES }
            form={ form }
            disabled={ isSomeCasesSubmitted }
          />
          <FormSelectInput
            name='applicant_relationship_to_main'
            label={ t('clients.formLabels.relationship') }
            data={ RELATIONSHIP }
            form={ form }
            disabled={ isSomeCasesSubmitted }
          />
        </Flex>
      )}
      {isSpain && (
        <>
          <Flex gap='4'>
            <FormInput
              name='applicant_employment_start_date'
              data-test='select-co-employment-start-date'
              label={ t('clients.formLabels.employmentStartDate') }
              type='date'
              form={ form }
              disabled={ isSomeCasesSubmitted }
            />
            <FormInput
              name='applicant_no_of_annual_employment_payments'
              label={ t('clients.formLabels.noOfAnnualPayments') }
              type='number'
              form={ form }
              disabled={ isSomeCasesSubmitted }
            />
          </Flex>
          <Flex gap='4'>
            <Box id='applicant_has_a_bonus' w='50%'>
              <Radio
                name='applicant_has_a_bonus'
                label={ t('clients.formLabels.receivesBonus') }
                data={ getYesNo() }
                defaultValue={ form.values.applicant_has_a_bonus ? 'yes' : 'no' }
                onValueChange={ ({ value }) => {
                  form.setFieldValue('applicant_has_a_bonus', value === 'yes');
                  setHasBonus(value === 'yes');
                } }
                disabled={ isSomeCasesSubmitted }
              />
            </Box>
          </Flex>
          {hasBonus && (
            <Flex gap='4'>
              <FormSelectInput
                name='applicant_bonus_frequency'
                label={ t('clients.formLabels.bonusType') }
                form={ form }
                data={ getBonusTypes() }
                disabled={ isSomeCasesSubmitted }
              />
              <FormInput
                name='applicant_bonus_value'
                label={ t('clients.formLabels.bonusValue') }
                type='number'
                form={ form }
                trail={ OPPORTUNITY_CURRENCY.EUR }
                disabled={ isSomeCasesSubmitted }
              />
            </Flex>
          )}
        </>
      )}
    </>
  );
};

export default OtherApplicantInfo;
